/**
 * STEEPNESS colour scale for the elevation charts' area fill (the shared
 * `ElevationProfile.vue` and `ElevationPanelChart.vue` both colour through
 * {@link steepnessColorForPercent}).
 *
 * The scale is DIVERGING and direction-aware, anchored on the shared flat
 * green:
 *
 *  - CLIMBS (positive grade) ramp warm: green -> yellow -> orange, matching
 *    the reference satellite-flyover elevation profile look. Warmer = harder
 *    climbing.
 *  - DESCENTS (negative grade) ramp cool: green -> teal -> blue, dropping to
 *    PURPLE at the steepest end so a dangerous descent still reads as
 *    "attention" without stealing the warm colours that mean climbing.
 *
 * Both ramps share the same deliberately non-linear stop spacing: dense stops
 * from 0 to 18% (where nearly all UK/Irish trail segments live), then a
 * compressed tail out to 45% so the top band doesn't visually saturate until
 * well past the 20%+ grades common on steep UK/Irish terrain.
 */

export interface SteepnessColorStop {
  /** Absolute grade %, i.e. `|grade|`, this stop represents. */
  absPercent: number;
  /** Colour (hex) at this stop. */
  color: string;
}

export type SteepnessBucketKey = 'gentle' | 'moderate' | 'steep' | 'verySteep';

export interface SteepnessBucket {
  key: SteepnessBucketKey;
  /** Short human label for a legend, e.g. `'Moderate'`. */
  label: string;
  /** Representative swatch colour for the legend. */
  color: string;
  /** Inclusive lower bound of `|grade%|` this bucket covers. */
  minAbsPercent: number;
  /** Exclusive upper bound of `|grade%|` this bucket covers. */
  maxAbsPercent: number;
}

/** Colour used for non-finite input (matches `gradient.ts`'s neutral colour). */
export const STEEPNESS_NEUTRAL_COLOR = '#9E9E9E';

/**
 * CLIMB stops (positive grades), keyed on `absPercent`. Ordered ascending;
 * {@link steepnessColorForPercent} interpolates linearly (in RGB space)
 * between the two stops bracketing a given `|grade%|`.
 */
export const STEEPNESS_COLOR_STOPS: SteepnessColorStop[] = [
  { absPercent: 0, color: '#6FCF6F' },
  { absPercent: 5, color: '#A8D95C' },
  { absPercent: 10, color: '#E8D24C' },
  { absPercent: 18, color: '#F4A94C' },
  { absPercent: 30, color: '#F08245' },
  { absPercent: 45, color: '#E8623C' },
];

/**
 * DESCENT stops (negative grades), keyed on `|grade%|` with the same spacing
 * as the climb ramp. Shares the flat green at 0, cools through teal/blue as
 * the descent steepens, and ends in purple so a dangerous descent still
 * demands attention at a glance.
 */
export const STEEPNESS_DESCENT_COLOR_STOPS: SteepnessColorStop[] = [
  { absPercent: 0, color: '#6FCF6F' },
  { absPercent: 5, color: '#6FC5CF' },
  { absPercent: 10, color: '#5CA8DC' },
  { absPercent: 18, color: '#4C7FD6' },
  { absPercent: 30, color: '#6A5ACD' },
  { absPercent: 45, color: '#8E44AD' },
];

/**
 * Discrete steepness buckets for the accessible summary / analytics.
 * Breakpoints (5/10/18%) are a reasonable default pending UX sign-off — see
 * the elevation-panel-v2 plan's Open Questions.
 */
export const STEEPNESS_BUCKETS: SteepnessBucket[] = [
  { key: 'gentle', label: 'Gentle', color: '#6FCF6F', minAbsPercent: 0, maxAbsPercent: 5 },
  { key: 'moderate', label: 'Moderate', color: '#A8D95C', minAbsPercent: 5, maxAbsPercent: 10 },
  { key: 'steep', label: 'Steep', color: '#E8D24C', minAbsPercent: 10, maxAbsPercent: 18 },
  { key: 'verySteep', label: 'Very steep', color: '#F4A94C', minAbsPercent: 18, maxAbsPercent: Infinity },
];

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;
}

function interpolateStops(stops: SteepnessColorStop[], abs: number): string {
  if (abs <= stops[0].absPercent) return stops[0].color;
  if (abs >= stops[stops.length - 1].absPercent) return stops[stops.length - 1].color;

  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (abs >= a.absPercent && abs <= b.absPercent) {
      if (abs === a.absPercent) return a.color;
      if (abs === b.absPercent) return b.color;
      const t = (abs - a.absPercent) / (b.absPercent - a.absPercent);
      const [r1, g1, b1] = hexToRgb(a.color);
      const [r2, g2, b2] = hexToRgb(b.color);
      return rgbToHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
    }
  }
  return STEEPNESS_NEUTRAL_COLOR;
}

/**
 * Map a SIGNED grade % to a continuous steepness colour: climbs (positive)
 * interpolate the warm {@link STEEPNESS_COLOR_STOPS}, descents (negative) the
 * cool {@link STEEPNESS_DESCENT_COLOR_STOPS} — both keyed on `|grade%|` and
 * clamped below the first stop and above the last. Non-finite input resolves
 * to {@link STEEPNESS_NEUTRAL_COLOR}.
 */
export function steepnessColorForPercent(percent: number): string {
  if (!Number.isFinite(percent)) return STEEPNESS_NEUTRAL_COLOR;
  const stops = percent < 0 ? STEEPNESS_DESCENT_COLOR_STOPS : STEEPNESS_COLOR_STOPS;
  return interpolateStops(stops, Math.abs(percent));
}

/**
 * Discrete bucket lookup for a grade % (direction-independent). Non-finite
 * input resolves to the `gentle` bucket.
 */
export function steepnessBucketForPercent(percent: number): SteepnessBucket {
  const abs = Number.isFinite(percent) ? Math.abs(percent) : 0;
  for (const bucket of STEEPNESS_BUCKETS) {
    if (abs >= bucket.minAbsPercent && abs < bucket.maxAbsPercent) return bucket;
  }
  return STEEPNESS_BUCKETS[STEEPNESS_BUCKETS.length - 1];
}

/**
 * Convenience: map a full `gradients[]` array (e.g. `ElevationChartData.gradients`
 * from `buildElevationChartData.ts`) to bucket keys, one per sample point.
 */
export function computeSteepnessBuckets(gradients: number[]): SteepnessBucketKey[] {
  return gradients.map((g) => steepnessBucketForPercent(g).key);
}
